import { useLocation, useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@Components/Breadcrumb/index';
import { GuestDetailsContainer } from '@Components/Guest';
import LoadingSpinner from '@Components/LoadingSpinner';
import { FishDetailsProvider, useFishDetails } from './FishDetailsContext';
import FishDetailBanner from './FishDetailBanner';
import FishDetailsOverlays from './FishDetailsOverlays';
import { useFishDetailParams } from './useFishDetailParams';

const BASE_TABS = ['overview', 'activity', 'messages', 'tags', 'profile'];

const breadcrumbFor = (variant) => {
  if (variant === 'team-member')
    return { label: 'Team Members', path: '/team-members' };
  if (variant === 'member') return { label: 'Members', path: '/members' };
  return { label: 'Customers', path: '/guests' };
};

function FishDetailsContent({ variant }) {
  const navigate = useNavigate();
  const { actor } = useFishDetails();
  const { tab, setTab, activitySubTab, setActivitySubTab } =
    useFishDetailParams();

  if (!actor) {
    return (
      <div className="dark -mx-8 -my-4 flex min-h-[calc(100%+2rem)] items-center justify-center bg-zinc-900">
        <LoadingSpinner />
      </div>
    );
  }

  const isTeamMember = variant === 'team-member';
  const tabs = [...BASE_TABS];
  if (isTeamMember) {
    tabs.push('employment');
  } else if (actor?.subscriptions?.length) {
    tabs.push('membership');
  }

  const crumb = breadcrumbFor(variant);

  return (
    <>
      {/* Negative margins cancel the app layout's main padding so the dark
          surface runs edge to edge instead of floating on the light background. */}
      <div className="dark -mx-8 -my-4 min-h-[calc(100%+2rem)] bg-zinc-900 text-zinc-100">
        <div className="px-4 pt-6 sm:px-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem
                onClick={() => navigate('/')}
                className="cursor-pointer opacity-50"
              >
                Dashboard
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem
                onClick={() => navigate(crumb.path)}
                className="cursor-pointer opacity-50"
              >
                {crumb.label}
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="mt-4">
          <FishDetailBanner variant={variant} />
        </div>

        {/* A tab can be selected from the URL before its data qualifies it for
            display; the container falls back to Overview. */}
        <GuestDetailsContainer
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          variant={variant}
          activitySubTab={activitySubTab}
          onActivitySubTabChange={setActivitySubTab}
          className=""
          headerClassName="bg-zinc-800 px-4 sm:px-8 dark:border-zinc-700"
          panelClassName="px-4 py-6 sm:px-8"
        />
      </div>

      {/* Kept outside the dark surface: Modal renders inline rather than in a
          portal, so nesting it here would give it dark text on a white panel. */}
      <FishDetailsOverlays />
    </>
  );
}

export default function FishDetails() {
  const { pathname } = useLocation();
  const variant = pathname.toLowerCase().includes('team-members')
    ? 'team-member'
    : pathname.toLowerCase().includes('members')
      ? 'member'
      : 'guest';

  return (
    <FishDetailsProvider>
      <FishDetailsContent variant={variant} />
    </FishDetailsProvider>
  );
}
